import { X } from "lucide-react";
import type { FunctionComponent, MouseEvent, ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

/** antd status names plus the presets the app uses. */
export type TagColor =
  "default" | "success" | "processing" | "warning" | "error" | "blue" | "green" | "red" | "orange" | "cyan" | "purple";

const NEUTRAL = "bg-noa-hover text-noa border-noa";
const SUCCESS = "bg-noa-success-subtle text-noa-success border-noa-success";
const INFO = "bg-noa-info-subtle text-noa-info border-noa-info";
const WARNING = "bg-noa-warning-subtle text-noa-warning border-noa-warning";
const DANGER = "bg-noa-danger-subtle text-noa-danger border-noa-danger";
// The palette is monochrome: the decorative hues map onto the brand slots.
const CYAN = "bg-noa-secondary-subtle text-noa-secondary border-noa-secondary";
const PURPLE = "bg-noa-primary-subtle text-noa-primary border-noa-primary";

const COLOR_CLASSES: Record<TagColor, string> = {
  default: NEUTRAL,
  success: SUCCESS,
  processing: INFO,
  warning: WARNING,
  error: DANGER,
  blue: INFO,
  green: SUCCESS,
  red: DANGER,
  orange: WARNING,
  cyan: CYAN,
  purple: PURPLE,
};

export interface TagProps {
  color?: TagColor;
  icon?: ReactNode;
  /** X button that hides the tag and calls `onClose` (call `preventDefault()` to keep it). */
  closable?: boolean;
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Default true; false drops the border. */
  bordered?: boolean;
  className?: string;
  children?: ReactNode;
}

/** Small label (antd `Tag` replacement). */
export const Tag: FunctionComponent<TagProps> = ({
  color = "default",
  icon,
  closable,
  onClose,
  bordered = true,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const [closed, setClosed] = useState(false);

  if (closed) return null;

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClose?.(event);
    if (!event.defaultPrevented) setClosed(true);
  };

  return (
    <span
      className={cn(
        "inline-flex h-auto max-w-full items-center gap-1 rounded px-[7px] text-xs leading-5 whitespace-nowrap",
        COLOR_CLASSES[color],
        bordered ? "border" : "border border-transparent",
        className
      )}
    >
      {icon && <span className="inline-flex shrink-0 [&_svg]:size-3">{icon}</span>}
      {children}
      {closable && (
        <button
          type="button"
          aria-label={t("common:close")}
          onClick={handleClose}
          className={cn(
            FOCUS_RING,
            "ms-0.5 inline-flex cursor-pointer items-center rounded-sm border-0 bg-transparent p-0 text-noa-muted transition-colors hover:text-noa"
          )}
        >
          <X className="size-2.5" />
        </button>
      )}
    </span>
  );
};
